import type Account from "@travily/types/models/Account";
import type Message from "@travily/types/models/MessagesMessage";
import type { MessageReaction } from "@ui/components/ReactionPicker";
import type { ReactNode } from "react";
import type { UploadFile } from "@/hooks/use-direct-upload-multiple";
import { ConversationDetailView } from "./components/ConversationDetailView";

export interface MessagesShowViewProps {
  /** The other party in the conversation, shown in the detail header. */
  account: Account;
  /** Signed-in user's account id. When absent (unauthenticated), the detail pane stays hidden. */
  currentUserId?: number;
  isSending: boolean;
  messageValue: string;
  messages: Message[] | undefined;
  onMessageChange: (value: string) => void;
  onAddFiles: (files: FileList) => void;
  onRemoveFile: (id: string) => void;
  onSendMessage: () => void;
  onTyping?: () => void;
  attachments: UploadFile[];
  onToggleReaction: (messageId: number, emoji: string) => void;
  reactions: Record<string, MessageReaction[]>;
  /** When the other side last opened the thread. Null until they have. */
  readAt: string | null;
  /** Names for the read receipt. Empty when the counterpart has no display name to show. */
  readers: string[];
  /** The counterpart's typing bubble, shown at the foot of the thread. */
  typingIndicator?: ReactNode;
}

/**
 * Presentational content region for the messages detail (conversation) view. The
 * conversation-list left pane and all Inertia wiring live in the container.
 */
export function MessagesShowView({
  account,
  currentUserId,
  isSending,
  messageValue,
  messages,
  onMessageChange,
  onAddFiles,
  onRemoveFile,
  onSendMessage,
  onTyping,
  attachments,
  onToggleReaction,
  reactions,
  readAt,
  readers,
  typingIndicator,
}: MessagesShowViewProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {currentUserId !== undefined && (
        <ConversationDetailView
          account={account}
          currentUserId={currentUserId}
          isSending={isSending}
          messageValue={messageValue}
          messages={messages}
          onMessageChange={onMessageChange}
          attachments={attachments}
          onAddFiles={onAddFiles}
          onRemoveFile={onRemoveFile}
          onSendMessage={onSendMessage}
          onToggleReaction={onToggleReaction}
          onTyping={onTyping}
          reactions={reactions}
          readAt={readAt}
          readers={readers}
          typingIndicator={typingIndicator}
        />
      )}
    </div>
  );
}
